import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaMoneyBillWave, FaWallet, FaCalendarAlt, FaPercent } from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

const FixedMonthlyIncomeCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [calculationType, setCalculationType] = useState('corpus'); // 'corpus' or 'income'
  
  // For calculating required corpus
  const [desiredMonthlyIncome, setDesiredMonthlyIncome] = useState(50000);
  const [withdrawalRate, setWithdrawalRate] = useState(4); // Safe withdrawal rate
  const [investmentReturn, setInvestmentReturn] = useState(8);
  const [inflationRate, setInflationRate] = useState(6);
  const [years, setYears] = useState(25);
  
  // For calculating monthly income from corpus
  const [corpusAmount, setCorpusAmount] = useState(10000000);
  
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (calculationType === 'corpus') {
      // Calculate required corpus for desired monthly income
      const annualIncome = desiredMonthlyIncome * 12;
      const realReturnRate = ((1 + investmentReturn / 100) / (1 + inflationRate / 100) - 1) * 100;
      
      // Required corpus based on withdrawal rate
      const basicCorpus = (annualIncome / (withdrawalRate / 100));
      
      // Adjust for inflation over years (present value of future income needs)
      const inflationAdjustedCorpus = basicCorpus * Math.pow(1 + inflationRate / 100, 5); // 5 years forward looking
      
      // Calculate sustainable corpus using perpetuity formula
      const sustainableCorpus = annualIncome / ((realReturnRate / 100) > 0 ? (realReturnRate / 100) : 0.01);
      
      // Calculate year-wise breakdown
      const yearlyBreakdown = [];
      let currentCorpus = basicCorpus;
      let currentMonthlyIncome = desiredMonthlyIncome;
      
      for (let year = 1; year <= Math.min(years, 30); year++) {
        const yearlyWithdrawal = currentMonthlyIncome * 12;
        const yearlyReturns = currentCorpus * (investmentReturn / 100);
        const endingCorpus = currentCorpus + yearlyReturns - yearlyWithdrawal;
        
        yearlyBreakdown.push({
          year,
          startingCorpus: currentCorpus,
          monthlyIncome: currentMonthlyIncome,
          yearlyWithdrawal,
          yearlyReturns,
          endingCorpus: Math.max(0, endingCorpus),
        });
        
        currentCorpus = Math.max(0, endingCorpus);
        currentMonthlyIncome *= (1 + inflationRate / 100); // Inflation adjusted income next year
        
        if (currentCorpus <= 0) break;
      }
      
      // Calculate corpus longevity
      let testCorpus = basicCorpus;
      let testIncome = desiredMonthlyIncome * 12;
      let longevityYears = 0;
      
      while (testCorpus > 0 && longevityYears < 100) {
        const returns = testCorpus * (investmentReturn / 100);
        testCorpus = testCorpus + returns - testIncome;
        testIncome *= (1 + inflationRate / 100);
        longevityYears++;
      }
      
      setResult({
        type: 'corpus',
        requiredCorpus: basicCorpus,
        inflationAdjustedCorpus,
        sustainableCorpus,
        desiredMonthlyIncome,
        desiredAnnualIncome: annualIncome,
        withdrawalRate,
        investmentReturn,
        inflationRate,
        realReturnRate,
        corpusLongevity: longevityYears,
        yearlyBreakdown,
        totalWithdrawal: yearlyBreakdown.reduce((sum, y) => sum + y.yearlyWithdrawal, 0),
      });
    } else {
      // Calculate monthly income from given corpus
      const annualIncomeFromCorpus = corpusAmount * (withdrawalRate / 100);
      const monthlyIncome = annualIncomeFromCorpus / 12;
      const realReturnRate = ((1 + investmentReturn / 100) / (1 + inflationRate / 100) - 1) * 100;
      
      // Year-wise breakdown with inflation adjustment
      const yearlyBreakdown = [];
      let currentCorpus = corpusAmount;
      let currentMonthlyIncome = monthlyIncome;
      
      for (let year = 1; year <= Math.min(years, 30); year++) {
        const yearlyWithdrawal = currentMonthlyIncome * 12;
        const yearlyReturns = currentCorpus * (investmentReturn / 100);
        const endingCorpus = currentCorpus + yearlyReturns - yearlyWithdrawal;
        
        yearlyBreakdown.push({
          year,
          startingCorpus: currentCorpus,
          monthlyIncome: currentMonthlyIncome,
          yearlyWithdrawal,
          yearlyReturns,
          endingCorpus: Math.max(0, endingCorpus),
        });
        
        currentCorpus = Math.max(0, endingCorpus);
        currentMonthlyIncome *= (1 + inflationRate / 100); // Inflation adjusted
        
        if (currentCorpus <= 0) break;
      }
      
      // Calculate corpus longevity
      let testCorpus = corpusAmount;
      let testIncome = annualIncomeFromCorpus;
      let longevityYears = 0;
      
      while (testCorpus > 0 && longevityYears < 100) {
        const returns = testCorpus * (investmentReturn / 100);
        testCorpus = testCorpus + returns - testIncome;
        testIncome *= (1 + inflationRate / 100);
        longevityYears++;
      }
      
      setResult({
        type: 'income',
        corpusAmount,
        monthlyIncome,
        annualIncome: annualIncomeFromCorpus,
        withdrawalRate,
        investmentReturn,
        inflationRate,
        realReturnRate,
        corpusLongevity: longevityYears,
        yearlyBreakdown,
        totalWithdrawal: yearlyBreakdown.reduce((sum, y) => sum + y.yearlyWithdrawal, 0),
      });
    }
  };

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setCalculationType('corpus');
    setDesiredMonthlyIncome(50000);
    setWithdrawalRate(4);
    setInvestmentReturn(8);
    setInflationRate(6);
    setYears(25);
    setCorpusAmount(10000000);
    calculate();
  };

  const formatCurrencyForPDF = (amount) => {
    return 'Rs. ' + Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const downloadPDF = () => {
    if (!result) return;

    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.setTextColor(37, 99, 235);
    doc.text('Fixed Monthly Income Calculator', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });
    
    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.5);
    doc.line(20, 32, 190, 32);
    
    let yPos = 45;
    doc.setFontSize(12);
    doc.setTextColor(0);
    
    if (result.type === 'corpus') {
      doc.text('Calculation Type: Required Corpus for Monthly Income', 20, yPos);
      yPos += 15;
      
      const inputData = [
        ['Desired Monthly Income', formatCurrencyForPDF(result.desiredMonthlyIncome)],
        ['Withdrawal Rate', `${result.withdrawalRate}%`],
        ['Expected Investment Return', `${result.investmentReturn}%`],
        ['Inflation Rate', `${result.inflationRate}%`],
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [['Parameter', 'Value']],
        body: inputData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      doc.setFontSize(14);
      doc.setTextColor(37, 99, 235);
      doc.text('Results', 20, yPos);
      yPos += 10;
      
      const resultData = [
        ['Required Corpus', formatCurrencyForPDF(result.requiredCorpus)],
        ['Inflation-Adjusted Corpus (5yr)', formatCurrencyForPDF(result.inflationAdjustedCorpus)],
        ['Annual Income Needed', formatCurrencyForPDF(result.desiredAnnualIncome)],
        ['Real Return Rate', `${result.realReturnRate.toFixed(2)}%`],
        ['Corpus Longevity', `${result.corpusLongevity} years`],
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [['Metric', 'Value']],
        body: resultData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
      });
    } else {
      doc.text('Calculation Type: Monthly Income from Corpus', 20, yPos);
      yPos += 15;
      
      const inputData = [
        ['Corpus Amount', formatCurrencyForPDF(result.corpusAmount)],
        ['Withdrawal Rate', `${result.withdrawalRate}%`],
        ['Expected Investment Return', `${result.investmentReturn}%`],
        ['Inflation Rate', `${result.inflationRate}%`],
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [['Parameter', 'Value']],
        body: inputData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      doc.setFontSize(14);
      doc.setTextColor(37, 99, 235);
      doc.text('Results', 20, yPos);
      yPos += 10;
      
      const resultData = [
        ['Monthly Income', formatCurrencyForPDF(result.monthlyIncome)],
        ['Annual Income', formatCurrencyForPDF(result.annualIncome)],
        ['Real Return Rate', `${result.realReturnRate.toFixed(2)}%`],
        ['Corpus Longevity', `${result.corpusLongevity} years`],
      ];
      
      doc.autoTable({
        startY: yPos,
        head: [['Metric', 'Value']],
        body: resultData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
      });
    }
    
    // Year-wise breakdown
    if (result.yearlyBreakdown && result.yearlyBreakdown.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.setTextColor(37, 99, 235);
      doc.text('Year-wise Breakdown', 20, 20);
      
      const breakdownData = result.yearlyBreakdown.slice(0, 20).map(y => [
        y.year,
        formatCurrencyForPDF(y.startingCorpus),
        formatCurrencyForPDF(y.monthlyIncome),
        formatCurrencyForPDF(y.yearlyReturns),
        formatCurrencyForPDF(y.endingCorpus),
      ]);
      
      doc.autoTable({
        startY: 30,
        head: [['Year', 'Starting Corpus', 'Monthly Income', 'Yearly Returns', 'Ending Corpus']],
        body: breakdownData,
        theme: 'striped',
        headStyles: { fillColor: [37, 99, 235] },
        styles: { fontSize: 8 },
      });
    }
    
    doc.save('fixed-monthly-income-calculation.pdf');
  };

  const pieData = result ? {
    labels: result.type === 'corpus' 
      ? ['Corpus Needed', 'Inflation Buffer (5yr)']
      : ['Principal', 'Annual Returns'],
    datasets: [{
      data: result.type === 'corpus'
        ? [result.requiredCorpus, result.inflationAdjustedCorpus - result.requiredCorpus]
        : [result.corpusAmount - (result.corpusAmount * result.investmentReturn / 100), result.corpusAmount * result.investmentReturn / 100],
      backgroundColor: ['#2563eb', '#10b981'],
      borderWidth: 0,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Fixed Monthly Income Calculator"
        description="Free Fixed Monthly Income Calculator - Plan regular income from investments in retirement."
        keywords="fixed monthly income calculator, monthly income planner, retirement income calculator, corpus for monthly income, regular income from investment"
        canonicalPath="/fixed-income"
      />
      <div className="calculator-header">
        <h1><FaMoneyBillWave /> Fixed Monthly Income Calculator</h1>
        <p>Calculate corpus required for monthly income or income from existing corpus</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>What do you want to calculate?</label>
            <div className="tabs" style={{ marginTop: '8px' }}>
              <button 
                className={`tab ${calculationType === 'corpus' ? 'active' : ''}`}
                onClick={() => setCalculationType('corpus')}
              >
                <FaWallet style={{ marginRight: '6px' }} />
                Required Corpus
              </button>
              <button 
                className={`tab ${calculationType === 'income' ? 'active' : ''}`}
                onClick={() => setCalculationType('income')}
              >
                <FaMoneyBillWave style={{ marginRight: '6px' }} />
                Monthly Income
              </button>
            </div>
          </div>

          {calculationType === 'corpus' ? (
            <div className="input-group">
              <label><FaRupeeSign /> Desired Monthly Income (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={desiredMonthlyIncome === '' ? '' : desiredMonthlyIncome}
                  onChange={(e) => setDesiredMonthlyIncome(e.target.value === '' ? '' : Number(e.target.value))}
                  min="1000"
                />
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="10000"
                  max="500000"
                  step="5000"
                  value={desiredMonthlyIncome || 0}
                  onChange={(e) => setDesiredMonthlyIncome(Number(e.target.value))}
                  style={{ '--value': `${((desiredMonthlyIncome || 0) / 500000) * 100}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="input-group">
              <label><FaWallet /> Available Corpus (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={corpusAmount === '' ? '' : corpusAmount}
                  onChange={(e) => setCorpusAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  min="100000"
                />
              </div>
              <div className="slider-container">
                <input
                  type="range"
                  min="1000000"
                  max="100000000"
                  step="100000"
                  value={corpusAmount || 0}
                  onChange={(e) => setCorpusAmount(Number(e.target.value))}
                  style={{ '--value': `${((corpusAmount || 0) / 100000000) * 100}%` }}
                />
              </div>
            </div>
          )}

          <div className="grid-2-cols">
            <div className="input-group">
              <label><FaPercent /> Withdrawal Rate (%)</label>
              <input
                type="number"
                value={withdrawalRate === '' ? '' : withdrawalRate}
                onChange={(e) => setWithdrawalRate(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                max="10"
                step="0.5"
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                Safe: 3-4% | Moderate: 5-6%
              </small>
            </div>

            <div className="input-group">
              <label><FaPercent /> Expected Returns (%)</label>
              <input
                type="number"
                value={investmentReturn === '' ? '' : investmentReturn}
                onChange={(e) => setInvestmentReturn(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                max="20"
                step="0.5"
              />
            </div>
          </div>

          <div className="grid-2-cols">
            <div className="input-group">
              <label><FaPercent /> Inflation Rate (%)</label>
              <input
                type="number"
                value={inflationRate === '' ? '' : inflationRate}
                onChange={(e) => setInflationRate(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="15"
                step="0.5"
              />
            </div>

            <div className="input-group">
              <label><FaCalendarAlt /> Plan Duration (Years)</label>
              <input
                type="number"
                value={years === '' ? '' : years}
                onChange={(e) => setYears(e.target.value === '' ? '' : Number(e.target.value))}
                min="5"
                max="50"
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-primary" onClick={calculate}>
              Calculate
            </button>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={downloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ 
                background: result.type === 'corpus' 
                  ? 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                  : 'linear-gradient(135deg, #10b981, #059669)'
              }}>
                <h3>{result.type === 'corpus' ? 'Required Corpus' : 'Monthly Income'}</h3>
                <div className="amount">
                  {result.type === 'corpus' 
                    ? formatCurrency(result.requiredCorpus)
                    : formatCurrency(result.monthlyIncome)
                  }
                </div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.type === 'corpus' 
                    ? `For ₹${desiredMonthlyIncome.toLocaleString('en-IN')}/month income`
                    : `From ₹${corpusAmount.toLocaleString('en-IN')} corpus`
                  }
                </p>
              </div>

              <div className="result-details">
                {result.type === 'corpus' ? (
                  <>
                    <div className="result-item">
                      <span className="label">Annual Income Needed</span>
                      <span className="value">{formatCurrency(result.desiredAnnualIncome)}</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Inflation-Adjusted Corpus (5yr)</span>
                      <span className="value" style={{ color: '#f59e0b' }}>{formatCurrency(result.inflationAdjustedCorpus)}</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Real Return Rate</span>
                      <span className="value">{result.realReturnRate.toFixed(2)}%</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="result-item">
                      <span className="label">Annual Income</span>
                      <span className="value">{formatCurrency(result.annualIncome)}</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Real Return Rate</span>
                      <span className="value">{result.realReturnRate.toFixed(2)}%</span>
                    </div>
                  </>
                )}
                <div className="result-item" style={{ background: result.corpusLongevity > 30 ? '#dcfce7' : '#fef3c7' }}>
                  <span className="label">Corpus Longevity</span>
                  <span className="value" style={{ color: result.corpusLongevity > 30 ? '#10b981' : '#f59e0b' }}>
                    {result.corpusLongevity >= 100 ? '100+ years (Sustainable)' : `${result.corpusLongevity} years`}
                  </span>
                </div>
                <div className="result-item">
                  <span className="label">Withdrawal Rate</span>
                  <span className="value">{result.withdrawalRate}% annually</span>
                </div>
              </div>

              {shouldRenderChart && pieData && (
                <div className="chart-container" style={{ maxWidth: '280px', margin: '20px auto' }}>
                  <Pie data={pieData} options={{ plugins: { legend: { position: 'bottom' } } }} />
                </div>
              )}

              {result.yearlyBreakdown && result.yearlyBreakdown.length > 0 && (
                <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '20px' }}>
                  <table className="schedule-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Monthly Income</th>
                        <th>Corpus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.yearlyBreakdown.map((item) => (
                        <tr key={item.year}>
                          <td>{item.year}</td>
                          <td>{formatCurrency(item.monthlyIncome)}</td>
                          <td>{formatCurrency(item.endingCorpus)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>💡 Understanding Fixed Monthly Income</h4>
        <ul>
          <li><strong>Safe Withdrawal Rate (SWR):</strong> 4% rule suggests you can withdraw 4% of your corpus annually without running out of money for 30+ years.</li>
          <li><strong>Corpus Formula:</strong> Required Corpus = Annual Income Need ÷ Withdrawal Rate</li>
          <li><strong>Real Return:</strong> Investment Return minus Inflation gives you real purchasing power growth.</li>
          <li><strong>Inflation Adjustment:</strong> Your monthly income needs will increase with inflation over time.</li>
        </ul>
        <h4 style={{ marginTop: '16px' }}>📊 Popular Fixed Income Options in India</h4>
        <ul>
          <li><strong>SCSS:</strong> 8.2% p.a., quarterly payout (for 60+ age)</li>
          <li><strong>POMIS:</strong> 7.4% p.a., monthly payout</li>
          <li><strong>PM Vaya Vandana:</strong> 7.4% p.a., monthly pension (for 60+ age)</li>
          <li><strong>SWP from Mutual Funds:</strong> Flexible withdrawal, market-linked returns</li>
          <li><strong>Annuity Plans:</strong> Guaranteed income, varies by provider</li>
        </ul>
      </div>
    </div>
  );
};

export default FixedMonthlyIncomeCalculator;
